/**
 * Shared contract for the deal real-time change channel (Server-Sent Events).
 *
 * Endpoint: `GET /deals/:dealId/events` — `Content-Type: text/event-stream`.
 *
 * The server pushes lightweight change *signals*, never deal/items state. On each signal the
 * client re-reads canonical state over HTTP:
 *   - `SYNC` / `DEAL`  → refetch the deal (the `deal` GraphQL query)
 *   - `SYNC` / `ITEMS` → reload the items (`GET /deals/:dealId/items/stream`)
 *
 * Wire format — each SSE message is a single `data:` line whose JSON body is a
 * {@link DealChangeNotification}, e.g.:
 *
 *     data: {"kind":"ITEMS","action":"itemsUpdated"}\n\n
 *
 * Lines starting with `:` are keep-alive comments and must be ignored by the client.
 *
 * This file is the single source of truth for the contract and is meant to be shared verbatim
 * between the deals backend and the frontend.
 */

/** Signal kinds pushed over the channel. */
export const DEAL_CHANGE_KINDS = ["SYNC", "DEAL", "ITEMS"] as const

export type DealChangeKind = (typeof DEAL_CHANGE_KINDS)[number]

/** The action that produced an `ITEMS` change. */
export const DEAL_ITEMS_ACTION_TYPES = [
    "datasourceProcessed",
    "itemsCreated",
    "itemsUpdated",
    "itemsDeleted",
] as const

export type DealItemsActionType = (typeof DEAL_ITEMS_ACTION_TYPES)[number]

/**
 * A single change signal (the JSON carried by an SSE `data:` line), discriminated on `kind`:
 *   - `SYNC`  — emitted on every (re)connect once the server is subscribed. Reload everything.
 *   - `DEAL`  — the deal itself changed. Refetch the deal.
 *   - `ITEMS` — deal items changed (see `action`). Reload the items.
 */
export type DealChangeNotification =
    | { kind: "SYNC" }
    | { kind: "DEAL" }
    | { kind: "ITEMS"; action: DealItemsActionType }
